import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'
import { statusKey, materialSummary, groupDocuments, groupTraining } from './complianceShared'

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const BASE = { responsive: true, maintainAspectRatio: false, animation: false }
const BAR_OPTIONS = { ...BASE, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { precision: 0 } } } }
const LEGEND_BAR_OPTIONS = { ...BAR_OPTIONS, plugins: { legend: { position: 'bottom' } } }
const DOUGHNUT_OPTIONS = { ...BASE, plugins: { legend: { position: 'bottom' } } }
const STACKED_OPTIONS = {
  ...BASE,
  plugins: { legend: { position: 'bottom' } },
  scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } } }
}

// light / pastel palette for charts
const C = {
  blue: '#93B4FF', purple: '#C4B5FD', orange: '#FFC999', teal: '#8FE0D8',
  sky: '#8CCBF5', green: '#86E0A8', cyan: '#7DD3FC', violet: '#D8B4FE', slate: '#CBD5E1'
}

// gradient palettes
const TONES = {
  green:  'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white',
  red:    'bg-gradient-to-br from-rose-400 to-rose-600 text-white',
  amber:  'bg-gradient-to-br from-amber-400 to-amber-600 text-white',
  blue:   'bg-gradient-to-br from-sky-400 to-blue-600 text-white',
  purple: 'bg-gradient-to-br from-violet-400 to-purple-600 text-white',
  teal:   'bg-gradient-to-br from-teal-400 to-cyan-600 text-white',
  plain:  'bg-white border border-slate-200 text-slate-900'
}

/* ────────────────────────────────────────────────
   One unified widget — guarantees identical look.
   - Fixed min-height so wrapping text never resizes cards.
   - Same padding / font sizes regardless of tone.
   ──────────────────────────────────────────────── */
function Widget({ label, value, sub, tone = 'blue', icon }) {
  return (
    <div className={`rounded-lg px-4 py-3 shadow-sm relative overflow-hidden min-h-[92px] flex flex-col justify-center ${TONES[tone]}`}>
      {icon ? (
        <div className='absolute right-2 top-2 opacity-20 text-2xl leading-none pointer-events-none'>{icon}</div>
      ) : null}
      <p className='text-[10px] uppercase tracking-wider font-semibold opacity-90 leading-tight'>{label}</p>
      <p className='mt-0.5 text-2xl font-extrabold leading-tight'>{value}</p>
      {sub ? <p className='mt-0.5 text-[10px] opacity-90 leading-tight truncate'>{sub}</p> : null}
    </div>
  )
}

function ChartCard({ title, children, accent = 'slate' }) {
  const accentBar = {
    slate: 'bg-slate-300', blue: 'bg-sky-400', purple: 'bg-violet-400',
    green: 'bg-emerald-400', orange: 'bg-amber-400', teal: 'bg-teal-400'
  }[accent]
  return (
    <div className='bg-white p-4 rounded-lg border border-slate-200 shadow-sm'>
      <div className='flex items-center gap-2 mb-4'>
        <span className={`w-1.5 h-5 rounded-full ${accentBar}`} />
        <h3 className='text-base font-semibold text-slate-800'>{title}</h3>
      </div>
      <div className='h-64'>{children}</div>
    </div>
  )
}

function AnalyticsTab({ totals, employeeDocuments, credentialRecords, trainingAssignments, credentialsLoading }) {
  const { documentTotal, categoryTotal, hiringTotal, onboardingTotal, submittedTotal } = totals

  const documentGroups = useMemo(() => groupDocuments(employeeDocuments), [employeeDocuments])
  const trainingGroups = useMemo(() => groupTraining(trainingAssignments), [trainingAssignments])
  const certificationTotal = useMemo(
    () => credentialRecords.filter((item) => item.type === 'certification').length,
    [credentialRecords]
  )

  /* ---- compliance charts ---- */
  const overviewChart = useMemo(() => ({
    labels: ['Documents', 'Categories', 'Hiring forms', 'Onboarding forms', 'Submitted forms', 'Employee documents', 'Training', 'Certifications'],
    datasets: [{
      label: 'Total',
      data: [documentTotal, categoryTotal, hiringTotal, onboardingTotal, submittedTotal, employeeDocuments.length, trainingAssignments.length, certificationTotal],
      backgroundColor: [C.blue, C.purple, C.orange, C.teal, C.sky, C.green, C.cyan, C.violet],
      borderRadius: 6,
      maxBarThickness: 42
    }]
  }), [documentTotal, categoryTotal, hiringTotal, onboardingTotal, submittedTotal, employeeDocuments.length, trainingAssignments.length, certificationTotal])

  const formsChart = useMemo(() => ({
    labels: ['Hiring forms', 'Onboarding forms'],
    datasets: [{ data: [hiringTotal, onboardingTotal], backgroundColor: [C.orange, C.teal], borderWidth: 0 }]
  }), [hiringTotal, onboardingTotal])

  const uploads = useMemo(() => {
    const mostByName = {}
    documentGroups.forEach((group) => {
      const titles = new Map()
      group.items.forEach((doc) => {
        const title = doc.title || doc.originalName || doc.fileName || 'Untitled'
        titles.set(title, (titles.get(title) || 0) + 1)
      })
      const [topTitle, topCount] = [...titles.entries()].sort((a, b) => b[1] - a[1])[0] || []
      mostByName[group.name] = topTitle ? `${topTitle} (${topCount})` : ''
    })
    return {
      chart: {
        labels: documentGroups.length ? documentGroups.map((g) => g.name) : ['No documents'],
        datasets: [{
          label: 'Documents',
          data: documentGroups.length ? documentGroups.map((g) => g.items.length) : [0],
          backgroundColor: C.sky, borderRadius: 6, maxBarThickness: 48
        }]
      },
      options: {
        ...BAR_OPTIONS,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              afterBody: (items) => {
                const text = mostByName[items?.[0]?.label]
                return text ? `Most uploaded: ${text}` : ''
              }
            }
          }
        }
      }
    }
  }, [documentGroups])

  const credentialChart = useMemo(() => {
    const tally = (check) => credentialRecords.filter((item) => item.type !== 'training' && check(item)).length
    const statusCount = (status) => trainingAssignments.filter((item) => (item.status || 'assigned') === status).length
    return {
      labels: ['Assigned', 'In progress', 'Completed', 'Pending', 'Verified', 'Rejected', 'Expired', 'Expiring', 'No expiry'],
      datasets: [
        {
          label: 'Training',
          data: [statusCount('assigned'), statusCount('in_progress'), statusCount('completed'), 0, 0, 0, 0, 0, 0],
          backgroundColor: C.blue, borderRadius: 6, maxBarThickness: 28
        },
        {
          label: 'Certifications',
          data: [
            0, 0, 0,
            tally((i) => (i.verification || 'pending') === 'pending'),
            tally((i) => i.verification === 'verified'),
            tally((i) => i.verification === 'rejected'),
            tally((i) => i.expiryTracking === 'Expired'),
            tally((i) => i.expiryTracking === 'Expiring'),
            tally((i) => !i.expiryTracking || i.expiryTracking === 'No expiry')
          ],
          backgroundColor: C.purple, borderRadius: 6, maxBarThickness: 28
        }
      ]
    }
  }, [credentialRecords, trainingAssignments])

  /* ---- training analytics ---- */
  const training = useMemo(() => {
    const counts = { assigned: 0, in_progress: 0, completed: 0 }
    let overdue = 0
    let dueSoon = 0
    let materialsTotal = 0
    let materialsDone = 0
    const today = dayjs().startOf('day')
    trainingAssignments.forEach((item) => {
      const key = statusKey(item)
      counts[key] += 1
      const materials = materialSummary(item)
      materialsTotal += materials.total
      materialsDone += materials.done
      if (key !== 'completed' && item.dueDate) {
        const due = dayjs(item.dueDate).startOf('day')
        if (due.isBefore(today)) overdue += 1
        else if (due.diff(today, 'day') <= 7) dueSoon += 1
      }
    })
    const total = trainingAssignments.length
    return {
      counts, overdue, dueSoon, materialsTotal, materialsDone, total,
      completionRate: total ? Math.round((counts.completed / total) * 100) : 0
    }
  }, [trainingAssignments])

  const statusChart = useMemo(() => ({
    labels: ['Assigned', 'In progress', 'Completed'],
    datasets: [{
      data: [training.counts.assigned, training.counts.in_progress, training.counts.completed],
      backgroundColor: [C.slate, C.blue, C.green], borderWidth: 0
    }]
  }), [training])

  const byEmployeeChart = useMemo(() => {
    const top = [...trainingGroups].sort((a, b) => b.items.length - a.items.length).slice(0, 8)
    const series = (label, key, color) => ({
      label, data: top.length ? top.map((g) => g.counts[key]) : [0], backgroundColor: color, maxBarThickness: 40
    })
    return {
      labels: top.length ? top.map((g) => g.name) : ['No assessments'],
      datasets: [
        series('Assigned', 'assigned', C.slate),
        series('In progress', 'in_progress', C.blue),
        series('Completed', 'completed', C.green)
      ]
    }
  }, [trainingGroups])

  const dash = credentialsLoading ? '—' : null

  /* ─────────────── Widget rows ─────────────── */

  // Row 1: the 5 summary numbers (plain white)
const row1 = [
  { id: 'documents',  label: 'Documents',           value: documentTotal,   tone: 'blue',   icon: '📄' },
  { id: 'categories', label: 'Document categories', value: categoryTotal,   tone: 'purple', icon: '🗂️' },
  { id: 'hiring',     label: 'Hiring forms',        value: hiringTotal,     tone: 'amber',  icon: '📝' },
  { id: 'onboarding', label: 'Onboarding forms',    value: onboardingTotal, tone: 'teal',   icon: '🚀' },
  { id: 'submitted',  label: 'Submitted forms',     value: submittedTotal,  tone: 'green',  icon: '📨' }
]

  // Row 2: training analytics (colored)
  const row2 = [
    { id: 'completion', label: 'Completion rate',     value: dash || `${training.completionRate}%`, sub: `${training.counts.completed} of ${training.total} assessments`, tone: 'green',  icon: '✅' },
    { id: 'overdue',    label: 'Overdue',             value: dash || training.overdue,              sub: 'Past due date, not completed',                                   tone: 'red',    icon: '⏰' },
    { id: 'duesoon',    label: 'Due in 7 days',       value: dash || training.dueSoon,              sub: 'Upcoming deadlines',                                             tone: 'amber',  icon: '📅' },
    { id: 'employees',  label: 'Employees assigned',  value: dash || trainingGroups.length,         sub: `${training.materialsDone}/${training.materialsTotal} materials`, tone: 'blue',   icon: '👥' }
  ]

  // Row 3: compliance summary (colored)
  const row3 = [
    { id: 'empdocs',     label: 'Employee documents', value: employeeDocuments.length, tone: 'blue',   icon: '📄' },
    { id: 'hiring2',     label: 'Hiring forms',       value: hiringTotal,              tone: 'amber',  icon: '📝' },
    { id: 'onboarding2', label: 'Onboarding forms',   value: onboardingTotal,          tone: 'teal',   icon: '🚀' },
    { id: 'certs',       label: 'Certifications',     value: certificationTotal,       tone: 'purple', icon: '🏆' }
  ]

  return (
    <div className='space-y-4'>
      {/* Row 1 — plain white summary cards, 5 across on xl */}
      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3'>
        {row1.map((w) => (
          <Widget key={w.id} label={w.label} value={credentialsLoading ? '—' : w.value} tone={w.tone} icon={w.icon} />
        ))}
      </div>

      {/* Row 2 — training analytics widgets, 4 across */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
        {row2.map((w) => (
          <Widget key={w.id} label={w.label} value={w.value} sub={w.sub} tone={w.tone} icon={w.icon} />
        ))}
      </div>

      {/* Row 3 — compliance summary widgets, 4 across */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
        {row3.map((w) => (
          <Widget key={w.id} label={w.label} value={w.value} tone={w.tone} icon={w.icon} />
        ))}
      </div>

      {/* Charts */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2'>
        <ChartCard title='Compliance overview' accent='blue'>
          <Bar data={overviewChart} options={BAR_OPTIONS} />
        </ChartCard>
        <ChartCard title='Hiring and onboarding forms' accent='orange'>
          <Doughnut data={formsChart} options={DOUGHNUT_OPTIONS} />
        </ChartCard>
        <ChartCard title='Documents uploaded by employee' accent='teal'>
          <Bar data={uploads.chart} options={uploads.options} />
        </ChartCard>
        <ChartCard title='Training and certifications' accent='purple'>
          <Bar data={credentialChart} options={LEGEND_BAR_OPTIONS} />
        </ChartCard>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
        <ChartCard title='Assessment status' accent='green'>
          <Doughnut data={statusChart} options={DOUGHNUT_OPTIONS} />
        </ChartCard>
        <ChartCard title='Assessments by employee' accent='blue'>
          <Bar data={byEmployeeChart} options={STACKED_OPTIONS} />
        </ChartCard>
      </div>
    </div>
  )
}

export default AnalyticsTab